import { useSyncExternalStore } from 'react';
import { LOGIN_FLAGS_EVENT, readLogin } from '../utils/localStorage';

// There is no module level copy of the login any more (sprint 17): one read
// when the bundle loaded went stale on every in-tab login, sign out and
// switch. Read it when it is needed, with readLogin, getToken or
// readSessionTokens from utils/localStorage.

/**
 * Whether the stored session is a staff account. 'unknown' means signed in
 * but the flag has not been fetched yet, which is the state of every session
 * that logged in before sprint 17 until the app's first user info call lands.
 */
export type StaffStatus = 'staff' | 'not-staff' | 'unknown';

export const staffStatus = (): StaffStatus => {
  const login = readLogin();
  if (!login || !login.access) {
    return 'not-staff';
  }
  if (typeof login.is_staff !== 'boolean') {
    return 'unknown';
  }
  return login.is_staff ? 'staff' : 'not-staff';
};

/**
 * 'Admin' for a staff account and 'all' otherwise, including while unknown.
 *
 * Staff is the same test the server applies to creating, changing and deleting
 * scripts. Until sprint 17 this looked for a role, is_admin or is_staff on the
 * stored login, none of which the login call returns, so nobody was offered
 * Edit or Delete. This only decides what is shown, the server is the gate.
 */
export const GetRole = (): 'Admin' | 'all' =>
  staffStatus() === 'staff' ? 'Admin' : 'all';

const subscribe = (onChange: () => void) => {
  window.addEventListener(LOGIN_FLAGS_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(LOGIN_FLAGS_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

/** The staff status, redrawn when the flags arrive or change. */
export const useStaffStatus = (): StaffStatus =>
  useSyncExternalStore(subscribe, staffStatus);

/** GetRole for components, redrawn when the flags arrive or change. */
export const useRole = (): 'Admin' | 'all' =>
  useStaffStatus() === 'staff' ? 'Admin' : 'all';
